import { useCallback } from 'react';
import { useEffect } from 'react';
import { useMemo } from 'react';
import { useState } from 'react';
import { useParams } from 'react-router';
import { CreateOrUpdateDatabaseRequestBodySchema } from 'libs/end-to-end/src/types/databases/create-our-update-database-request-body';
import { Database } from 'libs/end-to-end/src/types/databases/database';
import { Organization } from 'libs/end-to-end/src/types/organizations/organization';
import { Setter } from 'libs/end-to-end/src/ui-types';
import { useLanguage } from 'apps/sigma2-ui/src/lib/hooks/useLanguage';
import { getApiBaseUrl } from 'apps/sigma2-ui/src/constants/environment';
import { TouchTargetBehavior } from 'apps/sigma2-ui/src/components/ui/TouchTarget';
import { useSimpleReadPattern } from 'apps/sigma2-ui/src/lib/hooks/useSimpleReadPattern';
import { useSimpleWritePattern } from 'apps/sigma2-ui/src/lib/hooks/useSimpleWritePattern';
import { SaveIndicatorStatus } from 'apps/sigma2-ui/src/components/SaveIndicator';
import { useNavigateSafe } from 'apps/sigma2-ui/src/lib/hooks/useNavigateSafe';

export interface DatabasesNewPageHook {
  organizationId: string;
  organizationName: string;
  existingDatabases: Database[];
  databaseNameLabel: string;
  databaseName: string;
  setDatabaseName: Setter<string>;
  submitButtonLabel: string;
  submitButtonBehavior: TouchTargetBehavior;
  submitButtonDisabled: boolean;
  backButtonLabel: string;
  backButtonDisabled: boolean;
  backButtonBehavior: TouchTargetBehavior;
  saveIndicatorStatus: SaveIndicatorStatus;
}

export const useDatabasesNewPage = (): DatabasesNewPageHook => {
  const { organizationId } = useParams();
  const navigate = useNavigateSafe();

  if (!organizationId) {
    throw new Error('organizationId is required');
  }

  const [databaseName, setDatabaseName] = useState('');
  const language = useLanguage();

  const adminOrgHookGetUrlFn = useCallback(
    () => `${getApiBaseUrl()}/organizations/${organizationId}`,
    [organizationId],
  );

  const adminOrgHook = useSimpleReadPattern({
    getUrlFn: adminOrgHookGetUrlFn,
    canRead: true,
    responseSchema: Organization,
  });

  const patternGetUrlFn = useCallback(
    () => `${getApiBaseUrl()}/organizations/${organizationId}/databases`,
    [organizationId],
  );

  const pattern = useSimpleWritePattern({
    writeSchema: CreateOrUpdateDatabaseRequestBodySchema,
    responseSchema: Database,
    getUrlFn: patternGetUrlFn,
    canWrite: true,
    method: 'POST',
  });

  useEffect(() => {
    if (pattern.status === 'done' && pattern.saved && pattern.entity) {
      navigate(
        `/organizations/${organizationId}/databases/${pattern.entity.id}/data-sets`,
      );
    }
  }, [pattern, navigate, organizationId]);

  return useMemo(() => {
    return {
      organizationId,
      // TODO: Load me from the API.
      organizationName:
        adminOrgHook.status === 'done' ? adminOrgHook.entity.name : '',
      existingDatabases: [],
      databaseNameLabel: language.databaseNameLabel(),
      databaseName,
      setDatabaseName,
      saveIndicatorStatus: pattern.saveIndicatorStatus,
      submitButtonDisabled: !pattern.canWrite || databaseName.length <= 0,
      submitButtonLabel: 'Submit',
      submitButtonBehavior: {
        type: 'onClick',
        onClick: () => {
          if (!pattern.canWrite || databaseName.length <= 0) {
            return;
          }

          pattern.saveEntity({
            name: databaseName,
            description: '',
            organizationId,
          });
        },
      },
      backButtonDisabled: pattern.status === 'saving',
      backButtonLabel: 'Back',
      backButtonBehavior: {
        type: 'onClick',
        onClick: () => {
          navigate(`/organizations/${organizationId}/databases`);
        },
      },
    };
  }, [organizationId, adminOrgHook, language, databaseName, pattern, navigate]);
};
